import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { reduxForm, getFormValues, change } from 'redux-form';
import { t } from 'i18next';
import { format, subDays } from 'date-fns';
import { formatDisplayDate, isJSON, concatArrayDuplicateItems } from '../../utils/func';
import {
  apiGetOrdersAll, apiGetOrdersInProgress, apiGetOrdersCompleted, apiCancelOrder,
} from '../../services/orders';
import { routes } from '../../utils/constants';
import {
  ShipmentStatuses, ShipmentStatusCode, Divisions, PrintingLabels,
} from '../../utils/enums';
import Download from '../../assets/icons/Download.svg';
import TableIcon from '../../assets/icons/Table.svg';
import Filter from '../../assets/icons/Filter16x16.svg';
import ActionsBlack from '../../assets/icons/ActionsBlack.svg';
import XIcon from '../../assets/icons/x_icon.svg';
import { StyledMDBDropdownMenu } from '../_styledComponents/MDBDropdownToggle';
import { getDeliveryDate, get12HourTime } from '../../utils/dateTime';
import { ApplyFiltersBody, ApplyFiltersFooter } from './ApplyFilters';
import DownloadModal from './DownloadModal';
import { CustomizeBody, CustomizeFooter } from './Customize';
import Table from './Table/index';
import LeftSideBar from '../ReactComponent/LeftSideBar';
import {
  SORTING_DIRECTION,
  DEFAULT_COLUMNS,
  LABEL_COLUMNS_TRANSLATION_KEY,
  TABS,
  SERVICE_LEVELS_CODES,
  FILTERS_SERVICE_LEVEL,
  FILTERS_STATUSES,
  ADDITIONAL_COLUMNS,
  FILTER_MAPPING,
} from './utils';
import PrimaryButton from '../../lib/ui/buttons/PrimaryButton';
import SecondaryButton from '../../lib/ui/buttons/SecondaryButton';
import {
  Container,
  TitlesContainer,
  StyledMDBDropdownActions,
  StyledMDBDropdownToggle,
  DropdownItem,
  DropdownItemIcon,
  Title,
  SubTitle,
  LegacyContainer,
  LegacyButtonGroup,
  LegacyButton,
  Label,
  ModalBoxStyle,
  ModalBody,
  CloseContainer,
  ModalTitlesContainer,
  ModalTitle,
  ModalSubTitle,
  ButtonGroup,
  ModalError,
} from './css';
import { apiDownloadBol, apiDownloadPdfLabel } from '../../services/documents';
import AlertModal from '../ReactComponent/Modal/AlertModal';
import { ActionButton } from '../ReactComponent/Modal/css';
import { getPreferences, createPreferences, updatePreferences } from '../../services/users';
import ModifyOrderModal from '../CreateShipment/Modals/ModifyOrderModal';
import ActionsMenu from '../../lib/ui/actionsMenu';

const formName = 'ManageOrders';

const emptyFilterObject = {
  accountNumbers: [],
  statusCodes: [],
  serviceLevels: [],
  serviceTypes: [],
  fromDate: null,
  toDate: null,
};

const filterInitialValues = {
  [TABS.ALL]: emptyFilterObject,
  [TABS.IN_PROGRESS]: emptyFilterObject,
  [TABS.COMPLETED]: emptyFilterObject,
};
const initialValues = {
  filters: filterInitialValues,
  columns: DEFAULT_COLUMNS,
  rememberSelection: false,
};

const PAGE_SIZE = 10;

const defaultColumns = {
  [TABS.ALL]: DEFAULT_COLUMNS.ALL,
  [TABS.IN_PROGRESS]: DEFAULT_COLUMNS.IN_PROGRESS,
  [TABS.COMPLETED]: DEFAULT_COLUMNS.COMPLETED,
};

const Dropdown = ({
  // eslint-disable-next-line no-unused-vars
  activeTab, status, openBOL, openLabelSM, openLabelLG, cancelShipment, modifyOrder, orderId, visibleActions, statusCode, countryCode, shipmentNumber,
}) => {
  const actions = [
    {
      id: 'view-order',
      label: t('ViewShipmentDetails'),
      icon: 'ShipmentIcon',
      disabled: (status === ShipmentStatuses.Expired || status === ShipmentStatuses.UnderReview || status === ShipmentStatuses.NotYetPickedUp),
      action: () => window.open(`${routes.viewOrder}?probillNumber=${orderId}`),
      visible: (visibleActions && visibleActions.shipmentDetails),
    },
    {
      id: 'tracking-detais',
      label: t('TrackingDetails'),
      icon: 'TrackIcon',
      disabled: (status === ShipmentStatuses.Expired || status === ShipmentStatuses.UnderReview || status === ShipmentStatuses.NotYetPickedUp),
      action: () => window.open(`${routes.viewShipmentTracking}?orderId=${orderId}`),
      visible: (visibleActions && visibleActions.trackingDetails),
    },
    {
      id: 'download-bol',
      label: t('DownloadBOL'),
      icon: 'DownloadIcon',
      disabled: (statusCode !== ShipmentStatusCode.ENTRY && statusCode !== ShipmentStatusCode.AVAIL && statusCode !== ShipmentStatusCode.HOLD),
      action: openBOL,
      visible: (visibleActions && visibleActions.downloadBOL),
    },
    {
      id: 'download-labels-sm',
      label: t('DownloadLabelsSM'),
      icon: 'DownloadIcon',
      disabled: (statusCode !== ShipmentStatusCode.ENTRY && statusCode !== ShipmentStatusCode.AVAIL && statusCode !== ShipmentStatusCode.HOLD),
      action: openLabelSM,
      visible: (visibleActions && visibleActions.downloadLabels),
    },
    {
      id: 'download-labels-lg',
      label: t('DownloadLabelsLG'),
      icon: 'DownloadIcon',
      disabled: (statusCode !== ShipmentStatusCode.ENTRY && statusCode !== ShipmentStatusCode.AVAIL && statusCode !== ShipmentStatusCode.HOLD),
      action: openLabelLG,
      visible: (visibleActions && visibleActions.downloadLabels),
    },
    {
      id: 'duplicate-order',
      label: t('DuplicateOrder'),
      icon: 'DuplicateIcon',
      disabled: (status === ShipmentStatuses.UnderReview),
      action: () => window.open(
        `${routes.createShipment}?recreate=true&probillNumber=${orderId}&shipmentNumber=${shipmentNumber}`,
      ),
      visible: (visibleActions && visibleActions.duplicateOrder),
    },
    {
      id: 'modify-order',
      label: t('Modify'),
      icon: 'EditIcon',
      disabled: (status === ShipmentStatuses.UnderReview || status === ShipmentStatuses.Delivered),
      action: () => modifyOrder(),
      visible: (visibleActions && visibleActions.modifyOrder),
    },
    {
      id: 'cancel-order',
      label: t('CancelShipment'),
      icon: 'XIcon',
      disabled: (status === ShipmentStatuses.UnderReview || status === ShipmentStatuses.Delivered),
      action: cancelShipment,
      visible: (visibleActions && visibleActions.cancelShipment),
    },
  ];

  return (<ActionsMenu options={actions} />);
};

export const DropdownActions = ({ handleDownloadReportClick, toggleCustomizeMenu, toggleFilterMenu }) => (
  <StyledMDBDropdownActions className="dropdown">
    <StyledMDBDropdownToggle className="border-0">
      <img src={ActionsBlack} alt="Actions" />
      <span>{t('Actions')}</span>
    </StyledMDBDropdownToggle>
    <StyledMDBDropdownMenu className="text-white bg-dark" basic>
      <>
        <DropdownItem onClick={handleDownloadReportClick}>
          <DropdownItemIcon iconSrc={Download} />
          {t('DownloadReport')}
        </DropdownItem>
        <DropdownItem onClick={toggleCustomizeMenu}>
          <DropdownItemIcon iconSrc={TableIcon} />
          {t('Customize')}
        </DropdownItem>
        <DropdownItem onClick={toggleFilterMenu}>
          <DropdownItemIcon iconSrc={Filter} />
          {t('Filters')}
        </DropdownItem>
      </>
    </StyledMDBDropdownMenu>
  </StyledMDBDropdownActions>
);

const CancelModalContent = ({
  isAuthenticated, errorMessage, isCancelShipmentWarning, isCancelShipmentSuccess, closeModal, handleCancelShipmentRequest,
}) => {
  const LTLCustomerNumber = 'LTL: 1.866.Day.Ross (329-7677)';
  const CommerceCustomerNumber = 'Commerce: 1.877.726.3329';

  return (
    <>
      <ModalTitlesContainer>
        <ModalTitle>
          {!isCancelShipmentWarning
            ? t('CancelShipment')
            : isCancelShipmentWarning
              ? t('CancelShipmentWarning')
              : t('Error')}
        </ModalTitle>

        {errorMessage && !isCancelShipmentWarning ? (
          <ModalSubTitle>{errorMessage}</ModalSubTitle>
        ) : (
          <ModalError>
            <p><strong>{errorMessage}</strong></p>
            <p>{t('PleaseContactCustomerService')}</p>
            <p>{(errorMessage || !isAuthenticated) ? LTLCustomerNumber : t('')}</p>
            <p>{(errorMessage || !isAuthenticated) ? CommerceCustomerNumber : t('')}</p>
          </ModalError>
        )}
      </ModalTitlesContainer>

      <ButtonGroup style={{ marginTop: '64px' }}>
        {(errorMessage && isAuthenticated && !isCancelShipmentWarning && !isCancelShipmentSuccess) ? (
          <>
            <div className="col-sm-12 col-md">
              <SecondaryButton
                label={t('GoBack')}
                onClick={() => closeModal()}
                showArrow={false}
              />
            </div>
            <div className="col-sm-12 col-md">
              <PrimaryButton
                label={t('CancelShipment')}
                onClick={() => handleCancelShipmentRequest()}
              />
            </div>
          </>
        ) : (errorMessage && isAuthenticated && (isCancelShipmentWarning || isCancelShipmentSuccess)) ? (
          <div className="col-sm-12">
            <PrimaryButton
              label={t('Close')}
              onClick={() => closeModal()}
            />
          </div>
        ) : (errorMessage) ? (
          <div className="col-sm-12">
            <SecondaryButton
              label={t('Accept')}
              onClick={() => closeModal()}
            />
          </div>
        ) : null}
      </ButtonGroup>
    </>
  );
};

const ModifyModalContent = ({ closeModal, handleModify }) => (
  <>
    <ModalTitle>{t('WarningTitle')}</ModalTitle>

    <ModalError>
      <p>{t('ModifyAlert')}</p>
    </ModalError>

    <ButtonGroup style={{ marginTop: '64px' }}>
      <div className="col-sm-12 col-md">
        <SecondaryButton
          name="cancelButton"
          label={t('GoBack')}
          onClick={closeModal}
          showArrow={false}
        />
      </div>

      <div className="col-sm-12 col-md">
        <PrimaryButton
          name="cancelOrder"
          label={t('Continue')}
          onClick={handleModify}
        />
      </div>
    </ButtonGroup>
  </>
);

let ManageOrders = ({
  fields, dispatch, isAuthenticated, formData = { filters: filterInitialValues }, profileAccountNumbers, userId, division, invalid,
}) => {
  const [data, setData] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [inProgressOrders, setInProgressOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
  const [isCustomizeMenuOpen, setIsCustomizeMenuOpen] = useState(false);
  const [isDownloadReportModalOpen, setIsDownloadReportModalOpen] = useState(false);
  const [isDownloadErrorModalOpen, setIsDownloadErrorModalOpen] = useState(false);
  const [isCancelShipmentModalOpen, setIsCancelShipmentModalOpen] = useState(false);
  const [isModifyShipmentModalOpen, setIsModifyShipmentModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCancelShipmentWarning, setIsCancelShipmentWarning] = useState(false);
  const [isCancelShipmentSuccess, setIsCancelShipmentSuccess] = useState(false);
  const [cancelShipmentZipCode, setCancelShipmentZipCode] = useState(null);
  const [cancelShipmentOrderNumber, setCancelShipmentOrderNumber] = useState(null);
  const [modifyShipmentOrderNumber, setModifyShipmentOrderNumber] = useState(null);
  const [activeTab, setActiveTab] = useState(TABS.ALL);
  const [customizeColumns, setCustomizeColumns] = useState(defaultColumns);
  const [triggerUserPreferences, setTriggerUserPreferences] = useState(0);
  const [keySearch, setKeySearch] = useState('');
  const [fieldOrder, setFieldOrder] = useState({
    [TABS.ALL]: {},
    [TABS.IN_PROGRESS]: {},
    [TABS.COMPLETED]: {},
  });
  const [pageNumber, setPageNumber] = useState({
    [TABS.ALL]: 1,
    [TABS.IN_PROGRESS]: 1,
    [TABS.COMPLETED]: 1,
  });
  const [totalPages, setTotalPages] = useState({
    [TABS.ALL]: 1,
    [TABS.IN_PROGRESS]: 1,
    [TABS.COMPLETED]: 1,
  });

  const [userPreferences, setUserPreferences] = useState(null);
  const [currentSearch, setCurrentSearch] = useState({ all: '', progress: '', completed: '' });

  const [isInitialFilters, setIsInitialFilters] = useState(
    {
      [TABS.ALL]: false,
      [TABS.IN_PROGRESS]: false,
      [TABS.COMPLETED]: false,
    },
  );

  const [modifyShipmentNumber, setModifyShipmentNumber] = useState(null);

  const makeDefaultFilterObject = (activeTab) => ({
    accountNumbers: (profileAccountNumbers || []).slice(0, 20),
    statusCodes: activeTab === TABS.ALL ? null : FILTERS_STATUSES[activeTab].map((status) => status),
    serviceLevels: activeTab === TABS.ALL ? null : FILTERS_SERVICE_LEVEL[activeTab].map((serviceLevel) => serviceLevel),
    serviceTypes: null,
    fromDate: format(subDays(new Date(), 60), 'yyyy-MM-dd'),
    toDate: format(new Date(), 'yyyy-MM-dd'),
  });

  const defaultFilters = {
    filters: {
      [TABS.ALL]: makeDefaultFilterObject(TABS.ALL),
      [TABS.IN_PROGRESS]: makeDefaultFilterObject(TABS.IN_PROGRESS),
      [TABS.COMPLETED]: makeDefaultFilterObject(TABS.COMPLETED),
    },
  };

  const defaultTabColumns = ((formData && formData.columns && formData.columns[activeTab]) || DEFAULT_COLUMNS[activeTab]);
  const { field: fieldOrderingName, direction: fieldOrderingDirection } = fieldOrder[activeTab];

  const getServiceLevelCodes = (serviceLevels) => serviceLevels.reduce((acc, value) => {
    acc = acc.concat(SERVICE_LEVELS_CODES[value]);
    return acc;
  }, []);

  const getTotalPages = (numberOfOrders) => Math.floor((numberOfOrders / PAGE_SIZE) + ((numberOfOrders % PAGE_SIZE) === 0 ? 0 : 1));

  const openBOL = (shipmentNumber) => apiDownloadBol(shipmentNumber);

  const openLabelSM = (shipmentNumber) => apiDownloadPdfLabel(shipmentNumber, PrintingLabels.SM);

  const openLabelLG = (shipmentNumber) => apiDownloadPdfLabel(shipmentNumber, PrintingLabels.LG);

  const openCancelShipmentModal = (shipperZipCode, orderNumber) => {
    let errorMessage = t('CannotCancelMsg');

    if (isAuthenticated) {
      errorMessage = t('CancelShipmentMessage');
    }

    setErrorMessage(errorMessage);
    setCancelShipmentZipCode(shipperZipCode);
    setCancelShipmentOrderNumber(orderNumber);
    setIsCancelShipmentModalOpen(true);
  };

  const requestModifyOrder = (orderId, shipmentNumber) => {
    setModifyShipmentOrderNumber(orderId);
    setModifyShipmentNumber(shipmentNumber);
    setIsModifyShipmentModalOpen(true);
  };

  const excludeCancelledStatusCodes = (statusCodes) => {
    if (!statusCodes || !statusCodes.includes('CANCL')) {
      return statusCodes
        || FILTERS_STATUSES[TABS.ALL].filter((code) => code !== 'CANCL');
    }

    return statusCodes;
  };

  const convertDataFormat = (data) => data
    .filter((each) => each.billType !== 'CREDIT' && each.billType !== 'REBILL')
    .map((each) => ({
      proBillNumber: each.probillNumber,
      shipmentNumber: each.shipmentNumber,
      orderId: each.orderId,
      shipperName: each.from.name,
      shipperAddress: each.from.address1,
      shipperCity: each.from.city,
      shipperState: each.from.provinceCode,
      shipperZipCode: each.from.postalCode,
      consigneeName: each.to.name,
      consigneeAddress: each.to.address1,
      consigneeCity: each.to.city,
      consigneeState: each.to.provinceCode,
      consigneeZipCode: each.to.postalCode,
      estimatedDelivery: each.estimatedDeliveryDate && formatDisplayDate(each.estimatedDeliveryDate),
      delivered: each.deliveryDate && getDeliveryDate({ deliveryDate: each.deliveryDate, deliveryDateType: each.deliveryDateType }),
      created: each.createdTime && formatDisplayDate(each.createdTime),
      service: each.serviceLevelCode ?? '',
      status: each.status,
      statusPercentage: each.statusPercentage,
      statusColor: each.statusColor,
      billOfLading: each.billOfLading,
      originTerminal: each.originTerminal,
      destinationTerminal: each.destinationTerminal,
      palletsDelivery: each.pallets,
      piecesDelivery: each.pieces,
      weightDelivery: each.weight && each.weight.value,
      charge: each.charge && each.charge.value,
      poNumber: each.poNumber,
      customerReferenceNumber: each.customerReferenceNumber,
      deliveryAppointment: each.deliveryAppointment,
      actions: <Dropdown
        activeTab={activeTab}
        status={each.status}
        countryCode={each?.from?.countryCode}
        statusCode={each.statusCode}
        shipmentNumber={each.shipmentNumber}
        openBOL={() => openBOL(each.shipmentNumber)}
        openLabelSM={() => openLabelSM(each.shipmentNumber)}
        openLabelLG={() => openLabelLG(each.shipmentNumber)}
        orderId={each.orderId}
        cancelShipment={() => openCancelShipmentModal(each.from.postalCode, each.orderId)}
        modifyOrder={() => requestModifyOrder(
          each.orderId,
          each.shipmentNumber,
        )}
        visibleActions={each.actions}
      />,
    }));

  const fetchFullOrders = () => {
    const {
      accountNumbers, serviceLevels, serviceTypes, fromDate, toDate,
    } = defaultFilters.filters[activeTab];
    const inProgressFilterStatusCode = defaultFilters.filters[TABS.IN_PROGRESS].statusCodes;
    const completedFilterStatusCode = defaultFilters.filters[TABS.COMPLETED].statusCodes;
    const serviceLevelCodes = getServiceLevelCodes(serviceLevels || []);

    return Promise.all([
      apiGetOrdersAll(
        accountNumbers,
        FILTERS_STATUSES[TABS.ALL].filter((code) => code !== 'CANCL'),
        null,
        null,
        fromDate,
        toDate,
        PAGE_SIZE,
        pageNumber[TABS.ALL],
        fieldOrderingName,
        fieldOrderingDirection,
      ),
      apiGetOrdersInProgress(
        accountNumbers,
        inProgressFilterStatusCode,
        serviceLevelCodes,
        serviceTypes,
        fromDate,
        toDate,
        PAGE_SIZE,
        pageNumber[TABS.IN_PROGRESS],
        fieldOrderingName,
        fieldOrderingDirection,
      ),
      apiGetOrdersCompleted(
        accountNumbers,
        completedFilterStatusCode,
        serviceLevelCodes,
        serviceTypes,
        fromDate,
        toDate,
        PAGE_SIZE,
        pageNumber[TABS.COMPLETED],
        fieldOrderingName,
        fieldOrderingDirection,
      ),
    ]).then((res) => {
      if (res && res[0].status === 200 && res[1].status === 200 && res[2].status === 200) {
        const resAllOrders = res[0].data;
        const resInProgressOrders = res[1].data;
        const resCompletedOrders = res[2].data;

        const convertedAllOrders = convertDataFormat(resAllOrders.orderItems);
        const convertedInProgressOrders = convertDataFormat(resInProgressOrders.orderItems);
        const convertedCompletedOrders = convertDataFormat(resCompletedOrders.orderItems);

        setData(convertedAllOrders);
        setAllOrders(convertedAllOrders);
        setInProgressOrders(convertedInProgressOrders);
        setCompletedOrders(convertedCompletedOrders);
        setTotalPages({
          [TABS.ALL]: getTotalPages(resAllOrders.total),
          [TABS.IN_PROGRESS]: getTotalPages(resInProgressOrders.total),
          [TABS.COMPLETED]: getTotalPages(resCompletedOrders.total),
        });
      }
    }).finally(() => {
      setIsLoaded(true);
      setIsInitialFilters({
        [TABS.ALL]: true,
        [TABS.IN_PROGRESS]: true,
        [TABS.COMPLETED]: true,
      });
      dispatch(change(formName, 'filters', defaultFilters.filters));
    },
    );
  };

  const fetchAllOrders = () => {
    const {
      accountNumbers, statusCodes, serviceLevels, serviceTypes, fromDate, toDate,
    } = formData.filters[activeTab];
    const serviceLevelCodes = getServiceLevelCodes(serviceLevels || []);
    const shouldExcludeCanceled = !statusCodes || !statusCodes.includes('CANCL');

    apiGetOrdersAll(
      accountNumbers,
      excludeCancelledStatusCodes(statusCodes),
      serviceLevelCodes,
      serviceTypes,
      fromDate,
      toDate,
      PAGE_SIZE,
      pageNumber[TABS.ALL],
      fieldOrderingName,
      fieldOrderingDirection,
      keySearch,
    ).then((res) => {
      if (res && res.status === 200) {
        const convertedData = convertDataFormat(res.data.orderItems, shouldExcludeCanceled);
        setData(convertedData);
        setAllOrders(convertedData);
        setTotalPages({ ...totalPages, [TABS.ALL]: getTotalPages(res.data.total) });
        setCurrentSearch({ ...currentSearch, all: keySearch });
      }
    });
  };

  const fetchInProgressOrders = () => {
    const {
      accountNumbers, statusCodes, serviceLevels, serviceTypes, fromDate, toDate,
    } = formData.filters[activeTab];
    const serviceLevelCodes = getServiceLevelCodes(serviceLevels || []);
    const shouldExcludeCanceled = !statusCodes || !statusCodes.includes('CANCL');

    apiGetOrdersInProgress(
      accountNumbers,
      excludeCancelledStatusCodes(statusCodes),
      serviceLevelCodes,
      serviceTypes,
      fromDate,
      toDate,
      PAGE_SIZE,
      pageNumber[TABS.IN_PROGRESS],
      fieldOrderingName,
      fieldOrderingDirection,
      keySearch,
    ).then((res) => {
      if (res && res.status === 200) {
        const convertedData = convertDataFormat(res.data.orderItems, shouldExcludeCanceled);
        setData(convertedData);
        setInProgressOrders(convertedData);
        setTotalPages({ ...totalPages, [TABS.IN_PROGRESS]: getTotalPages(res.data.total) });
        setCurrentSearch({ ...currentSearch, progress: keySearch });
      }
    });
  };

  const fetchCompletedOrders = () => {
    const {
      accountNumbers, statusCodes, serviceLevels, serviceTypes, fromDate, toDate,
    } = formData.filters[activeTab];
    const serviceLevelCodes = getServiceLevelCodes(serviceLevels || []);
    const shouldExcludeCanceled = !statusCodes || !statusCodes.includes('CANCL');

    apiGetOrdersCompleted(
      accountNumbers,
      excludeCancelledStatusCodes(statusCodes),
      serviceLevelCodes,
      serviceTypes,
      fromDate,
      toDate,
      PAGE_SIZE,
      pageNumber[TABS.COMPLETED],
      fieldOrderingName,
      fieldOrderingDirection,
      keySearch,
    ).then((res) => {
      if (res && res.status === 200) {
        const convertedData = convertDataFormat(res.data.orderItems, shouldExcludeCanceled);
        setData(convertedData);
        setCompletedOrders(convertedData);
        setTotalPages({ ...totalPages, [TABS.COMPLETED]: getTotalPages(res.data.total) });
        setCurrentSearch({ ...currentSearch, completed: keySearch });
      }
    });
  };

  const fetchUserPreferences = async () => {
    const res = await getPreferences(userId, division);

    // User has preferences
    if (res && res.data && res.data.userId > 0) {
      const preferences = res.data;
      let columns = defaultColumns;

      // User has custom columns preferences
      if (preferences.customizeManageOrders) {
        try {
          if (isJSON(preferences.customizeManageOrders)) {
            columns = JSON.parse(preferences.customizeManageOrders);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`Error while trying to read user preferences: ${err}`);
        }
      }

      setCustomizeColumns(columns);
      dispatch(change(formName, 'columns', columns));
      setUserPreferences({ ...preferences, customizeManageOrders: columns });
    }
  };

  useEffect(() => {
    fetchUserPreferences();
    fetchFullOrders();
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    const condition = false;

    if (!condition) {
      switch (activeTab) {
        case TABS.IN_PROGRESS:
        // eslint-disable-next-line consistent-return
          return fetchInProgressOrders();
        case TABS.COMPLETED:
        // eslint-disable-next-line consistent-return
          return fetchCompletedOrders();
        case TABS.ALL:
        default:
        // eslint-disable-next-line consistent-return
          return fetchAllOrders();
      }
    }
  }, [fieldOrder, pageNumber]);

  useEffect(() => {
    setIsInitialFilters({ ...isInitialFilters, [activeTab]: false });
    setPageNumber({ ...pageNumber, [activeTab]: 1 });
  }, [keySearch]);

  useEffect(() => {
    switch (activeTab) {
      case TABS.IN_PROGRESS:
        if (currentSearch.progress !== keySearch && !(keySearch === '' && currentSearch.progress === '')) {
          setIsInitialFilters({ ...isInitialFilters, [activeTab]: false });
          setPageNumber({ ...pageNumber, [activeTab]: 1 });
        }

        setData(inProgressOrders);
        break;
      case TABS.COMPLETED:
        if (currentSearch.completed !== keySearch && !(keySearch === '' && currentSearch.completed === '')) {
          setIsInitialFilters({ ...isInitialFilters, [activeTab]: false });
          setPageNumber({ ...pageNumber, [activeTab]: 1 });
        }

        setData(completedOrders);
        break;
      default:
        if (currentSearch.all !== keySearch && !(keySearch === '' && currentSearch.all === '')) {
          setIsInitialFilters({ ...isInitialFilters, [activeTab]: false });
          setPageNumber({ ...pageNumber, [activeTab]: 1 });
        }

        setData(allOrders);
        break;
    }
  }, [activeTab]);

  useEffect(() => {
    if (userPreferences && formData && formData.rememberSelection) {
      const userId = (userPreferences && userPreferences.userId) ? userPreferences.userId : 0;
      const hasChanged = (JSON.stringify(formData.columns) !== JSON.stringify(userPreferences.customizeManageOrders));

      const convertTime = (obj) => {
        const hours = (obj.hours.toString().length === 1) ? `0${obj.hours}` : obj.hours;
        const minutes = (obj.minutes.toString().length === 1) ? `0${obj.minutes}` : obj.minutes;

        return `${hours}:${minutes} ${obj.amPm}`;
      };

      const obj = {
        ...userPreferences,
        customizeManageOrders: JSON.stringify(formData.columns),
        readyTime: convertTime(get12HourTime(userPreferences.readyTime)),
        closingTime: convertTime(get12HourTime(userPreferences.closingTime)),
      };

      if (hasChanged && userId === 0) {
        createPreferences(obj);
      }

      if (hasChanged && userId !== 0) {
        updatePreferences(obj);
      }
    }
  }, [triggerUserPreferences]);

  const updateStatusToFilter = (activeTab) => {
    if (!activeTab || !FILTERS_STATUSES) {
      return [];
    }

    const sttsRawObj = FILTERS_STATUSES[activeTab].map((status) => ({
      label: t(`Status${status}`),
      value: status,
    }));

    const stts = concatArrayDuplicateItems(sttsRawObj, 'label', 'value');
    return stts;
  };

  const numberOfFilters = useMemo(() => {
    let result = 0;
    if (formData && formData.filters[activeTab]) {
      Object.keys(formData.filters[activeTab]).forEach((key) => {
        if (formData.filters[activeTab][key] && formData.filters[activeTab][key].length) {
          const stts = updateStatusToFilter(activeTab);
          if (Array.isArray(formData.filters[activeTab][key])) {
            if (key === 'statusCodes') {
              // eslint-disable-next-line array-callback-return
              stts.map((object) => {
                if (object.value.split(',').every((v) => formData.filters[activeTab][key].includes(v))) {
                  result++;
                }
              });
            } else {
              result += formData.filters[activeTab][key].length;
            }
          } else {
            result++;
          }
        }
      });
    }

    return result;
  }, [formData.filters[activeTab], activeTab]);

  const handleLegacyButton = (division) => {
    window.open(`${routes.manageShipments}?division=${division}`);
  };

  const handleDownloadReportClick = () => {
    setIsDownloadReportModalOpen(true);
  };

  const closeDownloadReportModal = () => {
    setIsDownloadReportModalOpen(false);
  };

  const openDownloadErrorModal = () => {
    setIsDownloadReportModalOpen(false);
    setIsDownloadErrorModalOpen(true);
  };

  const closeDownloadErrorModal = () => {
    setIsDownloadErrorModalOpen(false);
  };

  const downloadErrorHeader = () => t('Error');
  const downloadErrorContent = () => t('DownloadReportErrorPopup');
  const downloadErrorActions = () => (
    <ActionButton className="active" iconRightArrow onClick={closeDownloadErrorModal}>
      {t('Okay')}
    </ActionButton>
  );

  const handleCancelShipmentRequest = () => {
    apiCancelOrder(cancelShipmentZipCode, cancelShipmentOrderNumber).then((res) => {
      setErrorMessage(res.message);
      setIsCancelShipmentWarning(res.hasReturnedError);
      setIsCancelShipmentSuccess(!res.hasReturnedError);
    });
  };

  const closeCancelShipmentModal = () => {
    setErrorMessage(null);
    setIsCancelShipmentWarning(false);
    setIsCancelShipmentSuccess(false);
    setCancelShipmentZipCode(null);
    setCancelShipmentOrderNumber(null);
    setModifyShipmentOrderNumber(null);
    setIsModifyShipmentModalOpen(false);
    setIsCancelShipmentModalOpen(false);
  };

  const accountNumbersArray = (profileAccountNumbers || []).map((accountNumber) => ({
    label: accountNumber,
    value: accountNumber,
  }));

  // Filters.
  const clearFilters = () => {
    dispatch(change(formName, `filters[${activeTab}]`, filterInitialValues[activeTab]));
  };

  const applyFilters = () => {
    setIsInitialFilters({ ...isInitialFilters, [activeTab]: false });
    setPageNumber({ ...pageNumber, [activeTab]: 1 });
    setIsFiltersMenuOpen(false);
  };

  const handleSearch = (searchStr) => {
    const str = (searchStr && searchStr.length > 0) ? searchStr : '';
    setKeySearch(str);
  };

  // Customize.
  const clearColumns = () => {
    setCustomizeColumns({ ...customizeColumns, [activeTab]: ['actions', 'shipmentNumber'] });
  };

  const applySelection = () => {
    dispatch(change(formName, 'columns', customizeColumns));
    setTriggerUserPreferences(triggerUserPreferences + 1);
    setIsCustomizeMenuOpen(false);
  };

  const changeActiveTab = (updatedTab) => {
    setActiveTab(updatedTab);
  };

  const onFieldOrderingClick = (columnName) => {
    if (columnName === 'estimatedDelivery' || columnName === 'proBillNumber' || columnName === 'actions') return;
    if (!fieldOrderingName && !fieldOrderingDirection) {
      setFieldOrder({
        ...fieldOrder,
        [activeTab]: {
          field: FILTER_MAPPING[columnName],
          fieldName: columnName,
          direction: SORTING_DIRECTION.ASC,
        },
      });
    } else if (fieldOrderingName && fieldOrderingDirection === SORTING_DIRECTION.ASC) {
      setFieldOrder({
        ...fieldOrder,
        [activeTab]: {
          field: FILTER_MAPPING[columnName],
          fieldName: columnName,
          direction: SORTING_DIRECTION.DESC,
        },
      });
    } else {
      setFieldOrder({ ...fieldOrder, [activeTab]: {} });
    }
  };

  const modifyOrder = () => {
    const orderId = modifyShipmentOrderNumber;
    const shipmentNumber = modifyShipmentNumber;

    window.open(
      `${routes.createShipment}?recreate=true&update=true&probillNumber=${orderId}&shipmentNumber=${shipmentNumber}`,
    );

    closeCancelShipmentModal();
  };

  const changeToDate = (value) => {
    dispatch(change(formName, `filters[${activeTab}].toDate`, value));
  };

  return (
    <Container>
      <Modal
        isOpen={isDownloadReportModalOpen || isCancelShipmentModalOpen || isModifyShipmentModalOpen}
        onRequestClose={isDownloadReportModalOpen ? closeDownloadReportModal : closeCancelShipmentModal}
        style={ModalBoxStyle}
        contentLabel="Modal"
      >
        <CloseContainer>
          <img src={XIcon} alt={t('Close')} onClick={isDownloadReportModalOpen ? closeDownloadReportModal : closeCancelShipmentModal} aria-hidden />
        </CloseContainer>
        <ModalBody>
          {isDownloadReportModalOpen ? (
            <DownloadModal
              columns={DEFAULT_COLUMNS[activeTab].concat(ADDITIONAL_COLUMNS)}
              defaultColumns={defaultTabColumns}
              accountNumbers={accountNumbersArray}
              closeModal={closeDownloadReportModal}
              onError={openDownloadErrorModal}
            />
          ) : (isModifyShipmentModalOpen)
            ? (
              <ModifyOrderModal
                secondaryAction={closeCancelShipmentModal}
                primaryAction={modifyOrder}
              />
            )
            : (
              <CancelModalContent
                isAuthenticated={isAuthenticated}
                errorMessage={errorMessage}
                isCancelShipmentWarning={isCancelShipmentWarning}
                isCancelShipmentSuccess={isCancelShipmentSuccess}
                closeModal={closeCancelShipmentModal}
                handleCancelShipmentRequest={handleCancelShipmentRequest}
              />
            )}
        </ModalBody>
      </Modal>

      <AlertModal
        isOpen={isDownloadErrorModalOpen}
        onClose={closeDownloadErrorModal}
        header={downloadErrorHeader}
        content={downloadErrorContent}
        actions={downloadErrorActions}
      />

      <LeftSideBar
        isOpen={isFiltersMenuOpen}
        toggleMenu={setIsFiltersMenuOpen}
        footer={(
          <ApplyFiltersFooter
            clearFilters={clearFilters}
            applyFilters={applyFilters}
            disabled={
              (formData.filters[activeTab].accountNumbers
                && (formData.filters[activeTab].accountNumbers.length === 0 || formData.filters[activeTab].accountNumbers.length > 20))
              || invalid
            }
          />
        )}
      >
        <ApplyFiltersBody
          filters={{
            serviceLevels: {
              name: `filters[${activeTab}].serviceLevels`,
              options: FILTERS_SERVICE_LEVEL[activeTab].map((serviceLevel) => ({
                label: t(`ServiceType${serviceLevel}`),
                value: serviceLevel,
              })),
            },
            statusCodes: {
              name: `filters[${activeTab}].statusCodes`,
              options: updateStatusToFilter(activeTab),
            },
            fromDate: {
              name: `filters[${activeTab}].fromDate`,
              filteredValues: null,
            },
            toDate: {
              name: `filters[${activeTab}].toDate`,
              filteredValues: null,
            },
            accountNumbers: {
              name: `filters[${activeTab}].accountNumbers`,
              options: accountNumbersArray,
            },
          }}
          data={formData.filters[activeTab]}
          changeToDate={changeToDate}
        />
      </LeftSideBar>

      <LeftSideBar
        isOpen={isCustomizeMenuOpen}
        toggleMenu={setIsCustomizeMenuOpen}
        footer={(
          <CustomizeFooter
            clearColumns={clearColumns}
            applySelection={applySelection}
          />
        )}
      >
        <CustomizeBody
          columns={customizeColumns[activeTab]}
          changeColumns={(newValue) => setCustomizeColumns({ ...customizeColumns, [activeTab]: newValue })}
          tabColumns={DEFAULT_COLUMNS[activeTab].concat(ADDITIONAL_COLUMNS)}
        />
      </LeftSideBar>

      <TitlesContainer>
        <Title>{fields.heading.value}</Title>
        <SubTitle>{fields.subTitle.value}</SubTitle>
      </TitlesContainer>

      <LegacyContainer>
        <Label>
          {t('ShipmentsBefore')}
          :
        </Label>
        <LegacyButtonGroup>
          <LegacyButton
            label={t('LTL')}
            onClick={() => handleLegacyButton(Divisions.Freight.name)}
          />
          <LegacyButton
            label={t('Commerce')}
            onClick={() => handleLegacyButton(Divisions.Sameday.name)}
          />
        </LegacyButtonGroup>
      </LegacyContainer>

      <Table
        data={data}
        columns={defaultTabColumns.map((column) => ({
          name: column,
          accessor: column,
          label: t(`Column${LABEL_COLUMNS_TRANSLATION_KEY[column]}`),
          disableSortBy: (column === 'actions' || column === 'estimatedDelivery' || column === 'proBillNumber'),
        }))}
        activeTab={activeTab}
        changeActiveTab={changeActiveTab}
        handleDownloadReportClick={handleDownloadReportClick}
        setIsCustomizeMenuOpen={setIsCustomizeMenuOpen}
        setIsFiltersMenuOpen={setIsFiltersMenuOpen}
        numberOfFilters={numberOfFilters}
        pageNumber={pageNumber[activeTab]}
        onPageChange={(newPageNumber) => setPageNumber({ ...pageNumber, [activeTab]: newPageNumber })}
        totalPages={totalPages[activeTab]}
        fieldOrder={fieldOrder}
        handleSearch={handleSearch}
        changeFieldOrder={onFieldOrderingClick}
      />
    </Container>
  );
};

const mstp = (state) => ({
  formData: getFormValues(formName)(state),
  isAuthenticated: state.profile.isAuthenticated,
  userId: state.profile.userId,
  division: state.profile.division,
  profileAccountNumbers: state.profile.accountNumbers,
  allShipments: state.user.allShipments,
});

ManageOrders.propTypes = {
  fields: PropTypes.object,
  dispatch: PropTypes.func,
  isAuthenticated: PropTypes.bool,
  formData: PropTypes.object,
  profileAccountNumbers: PropTypes.array,
  userId: PropTypes.number,
  division: PropTypes.string,
  invalid: PropTypes.bool,
};

ManageOrders = reduxForm({
  form: formName,
  initialValues,
})(ManageOrders);

export default connect(mstp)(ManageOrders);

Dropdown.propTypes = {
  activeTab: PropTypes.string,
  status: PropTypes.string,
  openBOL: PropTypes.func,
  openLabelSM: PropTypes.func,
  openLabelLG: PropTypes.func,
  cancelShipment: PropTypes.func,
  modifyOrder: PropTypes.func,
  orderId: PropTypes.string,
  shipmentNumber: PropTypes.string,
  visibleActions: PropTypes.object,
  statusCode: PropTypes.string,
  countryCode: PropTypes.string,
};

DropdownActions.propTypes = {
  handleDownloadReportClick: PropTypes.func,
  toggleCustomizeMenu: PropTypes.func,
  toggleFilterMenu: PropTypes.func,
};

CancelModalContent.propTypes = {
  isAuthenticated: PropTypes.bool,
  errorMessage: PropTypes.string,
  isCancelShipmentWarning: PropTypes.bool,
  isCancelShipmentSuccess: PropTypes.bool,
  closeModal: PropTypes.func,
  handleCancelShipmentRequest: PropTypes.func,
};

ModifyModalContent.propTypes = {
  closeModal: PropTypes.func,
  handleModify: PropTypes.func,
};
